import { useState, useMemo, useEffect } from 'react';
import {
  useResources,
  useCreateResource,
  useUpdateResource,
  useDeleteResource,
  useMapPeopleIcon,
  useResourceById,
} from '../../../hooks/queries';
import { useSites } from '../../../hooks/queries';
import {
  ResourceCreate,
  createResourceCreateObj,
  type Resource,
} from '../../../services';
import { useSearchParams } from 'react-router-dom';

export interface ResourceRow {
  [key: string]: unknown;
  id: string;
  name: string;
  type: string;
  latitude: string;
  longitude: string;
  room_name: string;
  room_id: string;
  school_id: string;
}

interface SiteGroup {
  siteId: number;
  siteName: string;
  resources: ResourceRow[];
}

function transformResources(resources: Resource[]): ResourceRow[] {
  return resources.map((r) => ({
    id: String(r.id),
    name: r.name,
    type: r.type || '-',
    latitude: r.latitude != null ? String(r.latitude) : '-',
    longitude: r.longitude != null ? String(r.longitude) : '-',
    room_name: r.room_name || '-',
    room_id: r.room != null ? String(r.room) : '-',
    school_id: r.school != null ? String(r.school) : '-',
  }));
}

// transformResources renders unset ids as the '-' placeholder, so unwind it
// before the edit payload reaches resourceFormSchema — Number('-') is NaN.
const parseOptionalId = (val: unknown): number | undefined => {
  if (val === undefined || val === null || val === '-') return undefined;
  const num = Number(val);
  return Number.isFinite(num) && num > 0 ? num : undefined;
};

export function useResourcesListData() {
  const [searchParams] = useSearchParams();
  const urlId = searchParams.get('id');
  const urlTab = searchParams.get('tab');
  const isResourcesTab = !urlTab || urlTab === 'resources';
  const [searchValue, setSearchValue] = useState('');
  const [expandedSites, setExpandedSites] = useState<Set<number>>(new Set());

  const { data: sitesData, isLoading: sitesLoading } = useSites({
    page_size: 100,
  });
  const { data: resourcesData, isLoading: resourcesLoading } = useResources({
    page_size: 500,
    search: searchValue || undefined,
  });

  const createMutation = useCreateResource();
  const updateMutation = useUpdateResource();
  const deleteMutation = useDeleteResource();
  const isLoading = sitesLoading || resourcesLoading;

  const [editOpen, setEditOpen] = useState(false);
  const [action, setAction] = useState<'add' | 'edit'>('add');
  const [currentRow, setCurrentRow] = useState<ResourceCreate>({
    name: '',
    type: '',
    school: undefined,
    room: undefined,
    id: undefined,
  });
  const { data: resourcesDataById } = useResourceById(Number(urlId));

  const handleDrawerClose = () => {
    setEditOpen(false);
    setAction('add');
    setCurrentRow(createResourceCreateObj());
  };

  const siteGroups = useMemo(() => {
    const isSingleResourceView = Boolean(
      urlId && isResourcesTab && resourcesDataById,
    );
    const resourcesList = isSingleResourceView
      ? [resourcesDataById as Resource]
      : resourcesData?.results;

    if (!sitesData?.results || !resourcesList) return [];

    const groups: SiteGroup[] = [];
    const resourcesBySchool = new Map<number, Resource[]>();

    for (const r of resourcesList) {
      const schoolId = r.school ?? 0;
      const list = resourcesBySchool.get(schoolId) || [];
      list.push(r);
      resourcesBySchool.set(schoolId, list);
    }

    for (const site of sitesData.results) {
      const siteResources = resourcesBySchool.get(site.id) || [];
      if (siteResources.length > 0 || (!searchValue && !isSingleResourceView)) {
        groups.push({
          siteId: site.id,
          siteName: site.name,
          resources: transformResources(siteResources),
        });
      }
    }

    return groups;
  }, [
    sitesData,
    resourcesData,
    resourcesDataById,
    searchValue,
    urlId,
    isResourcesTab,
  ]);

  const toggleSite = (siteId: number) => {
    setExpandedSites((prev) => {
      const next = new Set(prev);
      if (next.has(siteId)) {
        next.delete(siteId);
      } else {
        next.add(siteId);
      }
      return next;
    });
  };

  const { data: poiTypes } = useMapPeopleIcon();
  const poiTypesOptions = useMemo(() => {
    return (
      poiTypes?.results?.poi?.map((item) => ({
        label: String(item.display_name),
        value: String(item.name),
      })) ?? []
    );
  }, [poiTypes]);

  const siteOptions = useMemo(() => {
    return (
      sitesData?.results?.map((site) => ({
        label: site.name,
        value: String(site.id),
      })) ?? []
    );
  }, [sitesData]);

  const handleAddResource = () => {
    setEditOpen(true);
    setAction('add');
    setCurrentRow(createResourceCreateObj());
  };

  const handleSubmit = (values: ResourceCreate) => {
    const payload = {
      name: values.name,
      type: values.type,
      school: values.school,
      room: values.room,
    };
    if (action === 'add') {
      createMutation.mutate(payload, {
        onSuccess: () => {
          setEditOpen(false);
        },
      });
    } else {
      updateMutation.mutate(
        { id: Number(currentRow.id), data: payload },
        {
          onSuccess: () => {
            setEditOpen(false);
          },
        },
      );
    }
  };

  const handleEditResource = (row: ResourceRow) => {
    setEditOpen(true);
    setAction('edit');
    const payload = {
      id: Number(row.id),
      name: row.name,
      // '-' is the table's placeholder, not a POI type — it satisfies
      // z.string().min(1) and would otherwise be persisted verbatim.
      type: row.type === '-' ? '' : row.type,
      school: parseOptionalId(row.school_id),
      room: parseOptionalId(row.room_id),
    };
    setCurrentRow(payload);
  };

  const handleDeleteResource = async () => {
    await deleteMutation.mutateAsync(Number(currentRow.id));
    handleDrawerClose();
  };

  return {
    // State
    searchValue,
    setSearchValue,
    expandedSites,
    editOpen,
    action,
    currentRow,
    isLoading,

    // Data
    siteGroups,
    poiTypesOptions,
    siteOptions,

    // Mutation state
    createMutationPending: createMutation.isPending,
    updateMutationPending: updateMutation.isPending,

    // Handlers
    handleDrawerClose,
    toggleSite,
    handleAddResource,
    handleSubmit,
    handleEditResource,
    handleDeleteResource,
  };
}
